import { useState } from "react";
import {
  useConfirmProviderNoEffect,
  useObserveProviderCancellation,
} from "@/api/orders";
import { Button } from "@/components/ui/button";
import {
  type ProviderCancellation,
  providerCancellationText,
} from "@/lib/provider-cancellation";
import { toast } from "sonner";

export function ProviderCancellations({
  operations,
}: {
  operations?: ProviderCancellation[];
}) {
  const observe = useObserveProviderCancellation();
  if (!operations?.length) return null;
  const check = async (labelId: string) => {
    try {
      await observe.mutateAsync(labelId);
      toast.info("Provider status checked", {
        description:
          "Review the updated result. This check does not approve cancellation or issue a customer refund.",
      });
    } catch {
      toast.error("Could not confirm provider cancellation", {
        description:
          "The request remains unresolved. Check the recorded reservation with the provider; do not submit another void.",
      });
    }
  };
  return (
    <section
      className="space-y-2 rounded-md border p-3 text-left text-sm"
      aria-label="Provider cancellation status"
    >
      <h3 className="font-semibold">Provider label cancellation</h3>
      <p className="text-xs text-muted-foreground">
        EZX work holds, provider label validity, and customer refund settlement
        are separate.
      </p>
      {operations.map((operation) => (
        <div key={operation.operationId} className="space-y-1 border-t pt-2">
          <p className="font-medium">
            {providerCancellationText[operation.state]}
          </p>
          {operation.failureReason && (
            <p className="break-words text-xs">{operation.failureReason}</p>
          )}
          <details className="text-xs">
            <summary>Recorded reservation</summary>
            <dl className="break-all">
              <dt>Label</dt>
              <dd>{operation.labelId}</dd>
              <dt>Operation</dt>
              <dd>{operation.operationId}</dd>
              <dt>Provider reference</dt>
              <dd>{operation.providerReference}</dd>
            </dl>
          </details>
          <p className="text-xs text-muted-foreground">
            {operation.state === "CONFIRMED"
              ? "Label void confirmed. Complete cancellation review; customer refunds still require their own authorization and settlement."
              : operation.state === "READY"
                ? "Continue normal cancellation review to submit this reservation."
                : "EZX must check the recorded provider result before any retry. The label may still be usable."}
          </p>
          {operation.state !== "READY" && operation.state !== "CONFIRMED" && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={observe.isPending}
              onClick={() => void check(operation.labelId)}
            >
              Check provider status
            </Button>
          )}
          {["PENDING", "UNKNOWN", "REJECTED"].includes(operation.state) &&
            operation.updatedAt && (
              <ConfirmNoEffect
                key={operation.updatedAt}
                operation={operation}
              />
            )}
        </div>
      ))}
    </section>
  );
}

function ConfirmNoEffect({ operation }: { operation: ProviderCancellation }) {
  const confirm = useConfirmProviderNoEffect();
  const [evidence, setEvidence] = useState("");
  const [verified, setVerified] = useState(false);
  const submit = async () => {
    if (!verified || evidence.trim().length < 10 || !operation.updatedAt)
      return;
    try {
      await confirm.mutateAsync({
        labelId: operation.labelId,
        evidence: evidence.trim(),
        expectedUpdatedAt: operation.updatedAt,
      });
      toast.info("Terminal provider outcome recorded", {
        description:
          "Return to cancellation review. No new void or customer refund was submitted.",
      });
      setEvidence("");
      setVerified(false);
    } catch {
      toast.error("Could not record confirmation", {
        description:
          "Refresh the operation and verify the provider evidence. No retry was submitted.",
      });
    }
  };
  return (
    <details className="text-xs">
      <summary>Provider confirmed the request ended without effect</summary>
      <p>
        Use only when the provider explicitly confirms the original request is
        finished and caused no cancellation or refund. A label that still looks
        active is not enough.
      </p>
      <label className="block">
        Provider confirmation evidence
        <textarea
          aria-label="Provider confirmation evidence"
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
          className="mt-1 block w-full rounded border p-2"
          maxLength={2000}
        />
      </label>
      <label className="my-2 flex gap-2">
        <input
          type="checkbox"
          checked={verified}
          onChange={(event) => setVerified(event.target.checked)}
        />
        I verified the provider confirmed this request is terminal with no
        effect
      </label>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!verified || evidence.trim().length < 10 || confirm.isPending}
        onClick={() => void submit()}
      >
        Record terminal confirmation
      </Button>
    </details>
  );
}
